/**
 * DomainSetup Component
 * Manages custom domain configuration for author sites
 */
import { useState, type FormEvent, type ChangeEvent } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DomainStatus = "pending" | "active" | "failed";

export interface DomainSetupProps {
  customDomain: string | undefined;
  customDomainStatus: DomainStatus | undefined;
  onSave: (domain: string) => void;
  onRemove: () => void;
  isLoading: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const domainStatusStyles: Record<DomainStatus, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  active: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
};

const domainStatusLabels: Record<DomainStatus, string> = {
  pending: "Pending",
  active: "Active",
  failed: "Failed",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DomainSetup({
  customDomain,
  customDomainStatus,
  onSave,
  onRemove,
  isLoading,
}: DomainSetupProps) {
  const [newDomain, setNewDomain] = useState("");

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    setNewDomain(e.target.value);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = newDomain.trim();
    if (trimmed) {
      onSave(trimmed);
    }
  }

  function handleRemove() {
    onRemove();
  }

  // If no domain is set, show input form
  if (!customDomain) {
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="customDomain"
            className="block text-sm font-medium text-slate-700 mb-1"
          >
            Custom Domain
          </label>
          <div className="flex gap-3">
            <input
              id="customDomain"
              type="text"
              value={newDomain}
              onChange={handleInputChange}
              disabled={isLoading}
              placeholder="novels.example.com"
              className="flex-1 px-3 py-2 border border-slate-300 rounded-md bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <button
              type="submit"
              disabled={isLoading || !newDomain.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Domain
            </button>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Enter your custom domain to use instead of your subdomain.
          </p>
        </div>
      </form>
    );
  }

  // Domain is set - show domain info with status and DNS instructions
  return (
    <div className="space-y-4">
      {/* Domain Header with Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-slate-900 font-medium">{customDomain}</span>
          {customDomainStatus && (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${domainStatusStyles[customDomainStatus]}`}
            >
              {domainStatusLabels[customDomainStatus]}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={handleRemove}
          disabled={isLoading}
          className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Remove
        </button>
      </div>

      {/* DNS Instructions */}
      <div className="bg-slate-50 rounded-lg p-4">
        <h4 className="font-medium text-slate-900 mb-2">DNS Configuration</h4>
        <p className="text-sm text-slate-600 mb-3">
          Add this CNAME record to your domain's DNS settings:
        </p>
        <div className="font-mono text-sm bg-white p-3 rounded border border-slate-200">
          <div className="grid grid-cols-[80px_1fr] gap-1">
            <span className="text-slate-500">Type:</span>
            <span className="text-slate-900">CNAME</span>
            <span className="text-slate-500">Name:</span>
            <span className="text-slate-900">{customDomain}</span>
            <span className="text-slate-500">Value:</span>
            <span className="text-slate-900">reader.nrplatform.com</span>
          </div>
        </div>
        {customDomainStatus === "pending" && (
          <p className="mt-3 text-sm text-amber-600">
            DNS propagation can take up to 48 hours. The status will update
            automatically once your domain is verified.
          </p>
        )}
        {customDomainStatus === "failed" && (
          <p className="mt-3 text-sm text-red-600">
            Domain verification failed. Please check your DNS settings and try
            again.
          </p>
        )}
      </div>
    </div>
  );
}
